import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Paper, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { supabase } from '../supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * OAuth Callback Handler
 * 
 * This component handles the OAuth callback from e-commerce platforms.
 * Supports two flows:
 * 1. From our app: code + state (state references pending auth in DB)
 * 2. From marketplace: code + storeName (direct installation from ikas marketplace)
 */
const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Completing authorization...');
  const [details, setDetails] = useState(null);

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    // First check if we have pending OAuth from sessionStorage (after login redirect)
    let code = searchParams.get('code');
    let state = searchParams.get('state');
    let storeName = searchParams.get('storeName'); // ikas sends this for marketplace installs
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Check for pending OAuth from sessionStorage (user returned from login)
    const pendingOAuth = sessionStorage.getItem('oauth_pending');
    if (pendingOAuth && !code) {
      try {
        const pending = JSON.parse(pendingOAuth);
        code = pending.code;
        state = pending.state;
        storeName = pending.storeName;
        sessionStorage.removeItem('oauth_pending');
      } catch (e) {
        console.error('Failed to parse pending OAuth:', e);
      }
    }

    // Check for errors from OAuth provider
    if (error) {
      setStatus('error');
      setMessage('Authorization was denied');
      setDetails(errorDescription || error);
      return;
    }

    // Validate required params - need code + (state OR storeName)
    if (!code) {
      setStatus('error');
      setMessage('Invalid callback');
      setDetails('Missing authorization code. Please try connecting again.');
      return;
    }
    
    if (!state && !storeName) {
      setStatus('error');
      setMessage('Invalid callback');
      setDetails('Missing state or storeName parameter');
      return;
    }

    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        // User not logged in - store callback params and redirect to login
        sessionStorage.setItem('oauth_pending', JSON.stringify({ code, state, storeName }));
        // Build login URL with redirect back here
        const currentUrl = window.location.href;
        navigate(`/login?redirect=${encodeURIComponent(currentUrl)}`);
        return;
      }

      // Send callback to backend - include both state and storeName
      const response = await fetch(`${BACKEND_URL}/market-api/oauth/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          code, 
          state: state || null,
          store_name: storeName || null,
          platform_code: 'ikas'  // For marketplace installs without state
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.error || 'Failed to complete authorization');
      }

      setStatus('success');
      setMessage('Store connected successfully!');
      setDetails(data);

      // Redirect to stores page after 2 seconds
      setTimeout(() => {
        navigate('/my-stores');
      }, 2000);

    } catch (err) {
      console.error('OAuth callback error:', err);
      setStatus('error');
      setMessage('Failed to complete authorization');
      setDetails(err.message);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: '#f5f5f5'
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center' }}>
        {status === 'processing' && (
          <>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              {message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we complete the connection...
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom color="success.main">
              {message}
            </Typography>
            {details && (
              <Box sx={{ mt: 2, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>Store:</strong> {details.store_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Product import has been scheduled. You'll see your products shortly.
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Redirecting to your stores...
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom color="error.main">
              {message}
            </Typography>
            {details && (
              <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
                {details}
              </Alert>
            )}
            <Button
              variant="contained"
              onClick={() => navigate('/my-stores')}
              sx={{ mt: 3 }}
            >
              Back to Stores
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default OAuthCallback;
